/*
 * Copyright © 2024-present Mia s.r.l.
 * All rights reserved
 */

import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import './PreviewLogs.css';
import { devLog } from '../utils/devLog';
import { newlyReplayedEntries } from '../utils/mergeReplayedLogs';
import { getContainerApiUrl } from '../utils/previewSession';
import { backoffDelay, classifyStatusResponse } from '../utils/previewReadiness';

// Every stack routes through the one universal preview-runner now: the SSE stream URL and the polling-fallback
// status URL below both target it, through previewSession.getContainerApiUrl like every other runner call.

// How often to poll /status for logs when no real SSE stream is available
// (e.g. the universal Rust preview-runner, which only exposes a poll-based
// /status endpoint with buildLogs/recentLogs — no live /logs/stream route).
const POLL_INTERVAL_MS = 3000;
// Once the build is done AND nothing new has shown up for a few polls in a
// row, there's no reason to keep hammering /status every 3s forever — slow
// down to this cadence instead (real bug reported live: a completed
// session's tab kept polling at the fast rate indefinitely). Switches back
// to the fast interval immediately the moment genuinely new log content
// appears, so this never delays seeing real new activity by more than one
// slow tick.
const SLOW_POLL_INTERVAL_MS = 20000;
// How many consecutive no-new-content polls, once the build is done,
// before switching to the slow cadence.
const STEADY_POLLS_BEFORE_SLOWDOWN = 3;
// Before opening the log stream: wait this long, then ask /status whether the runner has the session yet (see the
// connect effect). 404s are retried with a doubling pause (1 s, 2 s, 4 s, 8 s…), at most MAX_SESSION_PROBES times.
const FIRST_PROBE_DELAY_MS = 1000;
const MAX_SESSION_PROBES = 6;

/**
 * PreviewLogs Component
 * Displays real-time logs from the preview container
 */
const PreviewLogs = ({sessionId, enabled = true, position = 'bottom', containerPort = 3001, onAskAI, isExpanded: externalIsExpanded, onToggleExpanded, onBuildError, hideUI = false, hasPendingError = false, pendingErrorText = null, onPendingErrorConsumed}) => {
  // Real live bug: a preview tab opened via the agent's `vfs_get_preview_url`
  // flow (Canvas.jsx's `showExternalPreview`, task #81) is fed a URL the
  // agent already built and is deliberately NOT backed by a real
  // preview-runner session this app is tracking — its `sessionId` is a
  // synthetic placeholder (`external-${serviceName}`), specifically so it
  // doesn't collide with a real one. This component had no guard for that:
  // it happily polled `/${containerName}/api/preview/external-.../status`
  // and `/logs/stream` every 3s forever, guaranteed 404 every time (there's
  // no such session, by design), on top of the SSE connect attempt. Treat a
  // synthetic sessionId the same as "no session" everywhere below.
  const hasRealSession = Boolean(sessionId) && !sessionId.startsWith('external-');
  const [logs, setLogs] = useState([]);
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  
  // Use external control if provided, otherwise use internal state
  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded;
  const setIsExpanded = onToggleExpanded || setInternalIsExpanded;
  const [isConnected, setIsConnected] = useState(false);
  // True while we've fallen back to polling /status instead of a live SSE
  // stream (e.g. the universal preview-runner has no /logs/stream endpoint).
  const [isPolling, setIsPolling] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'stdout', 'stderr', 'errors', 'client'
  const [expandedStackGroups, setExpandedStackGroups] = useState(() => new Set());
  // buildStatus received in the initial SSE payload from the server.
  // 'completed' means the last build succeeded; anything else means errors may be active.
  const [historicalBuildStatus, setHistoricalBuildStatus] = useState(null);
  const logsContainerRef = useRef(null);
  const eventSourceRef = useRef(null);
  // Bounded SSE-reconnect budget — reset per session (see the connect/
  // disconnect effect below), not per attempt, so a genuinely new preview
  // session gets its own fresh couple of tries.
  const streamRetryCountRef = useRef(0);
  const pollTimerRef = useRef(null);
  // Consecutive 404s from /status — distinguishes a session that hasn't
  // registered yet (a brief race right after render starts, self-resolves
  // within a couple of ticks) from one that's permanently gone (reaped by
  // the runner's idle SESSION_TTL_MS, ~30min default — a tab left open past
  // that would otherwise poll a dead session every 3s forever, confirmed
  // live: 623 failed status/stream requests on a long-lived tab). Reset on
  // any successful poll or session change; stop polling once it's clearly
  // not coming back rather than retrying indefinitely.
  const consecutiveNotFoundRef = useRef(0);
  const MAX_CONSECUTIVE_NOT_FOUND = 5;
  // Fingerprint of the last poll's buildLogs+recentLogs content, and how
  // many consecutive polls have come back identical once the build is
  // done — drives the fast->slow polling-cadence switch above.
  const lastLogsSignatureRef = useRef(null);
  const steadyPollCountRef = useRef(0);
  const isSlowPollingRef = useRef(false);
  const buildErrorFiredRef = useRef(false);
  // The last 'initial' (replayed-buffer) snapshot the stream sent us, used
  // to merge a later reconnect's replay instead of wholesale-replacing the
  // log list with it (see connectToStream's onmessage below).
  const lastReplayedLogsRef = useRef([]);
  // Keep stable refs so we never need onBuildError / logs in effect deps
  const onBuildErrorRef = useRef(onBuildError);
  const latestLogsRef = useRef(logs);

  // Keep refs in sync with latest prop/state values on every render
  useEffect(() => {
    onBuildErrorRef.current = onBuildError;
  });
  useEffect(() => {
    latestLogsRef.current = logs;
  });

  // Reset the "already fired" flag and historical build status whenever a new preview session starts
  useEffect(() => {
    buildErrorFiredRef.current = false;
    setHistoricalBuildStatus(null);
    consecutiveNotFoundRef.current = 0;
    lastLogsSignatureRef.current = null;
    steadyPollCountRef.current = 0;
    isSlowPollingRef.current = false;
  }, [sessionId]);

  // Scroll to bottom when new logs arrive and autoScroll is enabled
  useEffect(() => {
    if (autoScroll && logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  // Listen for client-side errors from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Only accept messages from our iframe origin
      if (!event.data || event.data.source !== 'mia-flow-preview-client') {
        return;
      }

      const {type, message, stack, timestamp} = event.data;
      
      // Add client error to logs
      const logEntry = {
        type: 'client-error',
        line: stack || message,
        timestamp: timestamp || new Date().toISOString(),
      };

      setLogs(prev => [...prev, logEntry]);
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Stop the /status polling fallback (used when no live SSE stream is available)
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // pollStatusOnce needs to re-schedule ITS OWN interval (to switch
  // cadence, see below) without recreating itself every render — a stable
  // ref to the latest version sidesteps the chicken-and-egg dependency
  // that would otherwise create. Declared before pollStatusOnce itself
  // purely for readability; the ref is only ever dereferenced at call
  // time (a later poll tick), well after this whole render has finished
  // and the effect below has populated it.
  const pollStatusOnceRef = useRef(null);

  // Fetch /status once and merge its buildLogs/recentLogs into the log view.
  // This is the fallback path for backends (e.g. the universal preview-runner)
  // that don't implement a real /logs/stream SSE endpoint — it keeps the panel
  // from being stuck forever on "Connecting to log stream..." with zero content.
  const pollStatusOnce = useCallback(async () => {
    if (!hasRealSession) return;

    // The same URL the render and its status poll use (previewSession.getContainerApiUrl). This used to decide by
    // the page's hostname alone, so a built Flow served on localhost (a kind cluster) polled `/universal/...`, which
    // only the Vite dev server proxies: the gateway answered 302 to /flow/ and the "status" was Flow's HTML.
    const statusUrl = getContainerApiUrl(containerPort, `/api/preview/${encodeURIComponent(sessionId)}/status`);

    try {
      const res = await fetch(statusUrl, { headers: { Accept: 'application/json' }, redirect: 'manual' });
      const kind = classifyStatusResponse(res);
      if (kind === 'signed-out') {
        // A redirect, a refusal or a page: not the runner. Never poll that forever.
        consecutiveNotFoundRef.current += 1;
        if (consecutiveNotFoundRef.current >= MAX_CONSECUTIVE_NOT_FOUND) {
          stopPolling();
        }
        return;
      }
      if (!res.ok) {
        // Only a 404 means "this session doesn't exist" (vs. a 5xx/network
        // hiccup, which is worth retrying) — count those specifically so a
        // session that's genuinely gone (reaped by the runner's idle TTL)
        // stops polling instead of hammering it every 3s forever.
        if (res.status === 404) {
          consecutiveNotFoundRef.current += 1;
          if (consecutiveNotFoundRef.current >= MAX_CONSECUTIVE_NOT_FOUND) {
            stopPolling();
          }
        }
        return;
      }
      consecutiveNotFoundRef.current = 0;
      const data = await res.json();
      const session = data && data.session;
      if (!session) return;

      setHistoricalBuildStatus(session.buildStatus || null);

      const buildLogEntries = (session.buildLogs || []).map(entry => ({
        type: 'stdout',
        line: entry.message,
        timestamp: entry.timestamp,
      }));
      const recentStdout = (session.recentLogs?.stdout || []).map(line => ({
        type: 'stdout',
        line,
        isHistorical: true,
        timestamp: null,
      }));
      const recentStderr = (session.recentLogs?.stderr || []).map(line => ({
        type: 'stderr',
        line,
        isHistorical: true,
        timestamp: null,
      }));

      // Snapshot-replace the polled portion of the log view each cycle
      // (both buildLogs and recentLogs are bounded, server-side buffers),
      // but keep any client-side error entries we've collected separately.
      setLogs(prev => [
        ...prev.filter(log => log.type === 'client-error'),
        ...buildLogEntries,
        ...recentStdout,
        ...recentStderr,
      ]);

      // Fast-poll while the build is actually in flight or output keeps
      // changing; once it's done AND a few polls in a row come back with
      // exactly the same content, drop to the slow cadence instead of
      // continuing to hammer /status every 3s forever — see
      // SLOW_POLL_INTERVAL_MS's own comment.
      const isTerminal = ['ready', 'completed', 'failed', 'error'].includes(session.buildStatus);
      const signature = `${buildLogEntries.length}|${recentStdout.length}|${recentStderr.length}|${buildLogEntries.at(-1)?.line}|${recentStdout.at(-1)?.line}|${recentStderr.at(-1)?.line}`;
      const unchanged = signature === lastLogsSignatureRef.current;
      lastLogsSignatureRef.current = signature;

      if (isTerminal && unchanged) {
        steadyPollCountRef.current += 1;
      } else {
        steadyPollCountRef.current = 0;
      }

      const shouldSlow = steadyPollCountRef.current >= STEADY_POLLS_BEFORE_SLOWDOWN;
      if (shouldSlow !== isSlowPollingRef.current && pollTimerRef.current) {
        isSlowPollingRef.current = shouldSlow;
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = setInterval(pollStatusOnceRef.current, shouldSlow ? SLOW_POLL_INTERVAL_MS : POLL_INTERVAL_MS);
      }
    } catch {
      // Network hiccup — just try again on the next poll tick.
    }
  }, [hasRealSession, sessionId, containerPort, stopPolling]);

  useEffect(() => {
    pollStatusOnceRef.current = pollStatusOnce;
  }, [pollStatusOnce]);

  const startPolling = useCallback(() => {
    if (pollTimerRef.current) return; // already polling
    setIsPolling(true);
    pollStatusOnce();
    pollTimerRef.current = setInterval(pollStatusOnce, POLL_INTERVAL_MS);
  }, [pollStatusOnce]);

  // Connect to log stream
  const connectToStream = useCallback(() => {
    if (!hasRealSession || !enabled) {
      return;
    }

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      // Same routing as the render and the status poll (see pollStatusOnce).
      const streamUrl = getContainerApiUrl(containerPort, `/api/preview/${encodeURIComponent(sessionId)}/logs/stream`);

      const eventSource = new EventSource(streamUrl);

      eventSource.onopen = () => {
        // eslint-disable-next-line no-console
        devLog('📡 Connected to log stream');
        setIsConnected(true);
        // A real SSE stream is up — stop the polling fallback if it was running.
        stopPolling();
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'initial') {
            // A replayed buffer — we don't have per-line timestamps for it.
            // Mark it as replayed so it renders as "replayed" instead of a fake time.
            // The server also sends buildStatus so we know if these errors are already resolved.
            setHistoricalBuildStatus(data.buildStatus || null);
            const initialLogs = [
              ...data.stdout.map(line => ({
                type: 'stdout',
                line,
                isHistorical: true,
                timestamp: null,
              })),
              ...data.stderr.map(line => ({
                type: 'stderr',
                line,
                isHistorical: true,
                timestamp: null,
              })),
            ];
            // Live-reported: this 'initial' replay isn't sent only once at
            // first connect — it's re-sent on every reconnect (a network
            // blip, or the dev server process itself restarting), and each
            // one is the FULL buffer accumulated so far, not just what was
            // missed. Replacing the whole log list with it (the original
            // behavior) discarded already-shown live lines and their real
            // timestamps, and could even make the list render out of
            // chronological order. Merge in only the genuinely new suffix
            // instead — see newlyReplayedEntries's own doc for why a
            // prefix diff, not a content Set.
            const newlyReplayedLogs = newlyReplayedEntries(lastReplayedLogsRef.current, initialLogs);
            lastReplayedLogsRef.current = initialLogs;
            setLogs(prev => (prev.length === 0 ? initialLogs : [...prev, ...newlyReplayedLogs]));
          } else {
            // New log line received
            setLogs(prev => [...prev, data]);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error parsing log message:', err);
        }
      };

      eventSource.onerror = (error) => {
        // Suppress verbose error logging
        // console.error('❌ Log stream error:', error);
        setIsConnected(false);
        eventSource.close();

        // The backend may not have a real SSE stream at all (e.g. the
        // universal preview-runner only exposes poll-based /status — it has
        // NO /logs/stream endpoint whatsoever, confirmed tonight). Rather
        // than sit stuck on "Connecting..." forever, start polling /status
        // immediately so the panel shows real content.
        startPolling();

        // Retry a bounded number of times only — a backend with no SSE
        // endpoint at all fails every single retry identically, so retrying
        // forever every 5s just means a permanent, pointless failed-request
        // loop for the lifetime of the preview (confirmed live: the network
        // tab showed this firing continuously). EventSource's onerror can't
        // distinguish "transient network blip, worth retrying" from "this
        // endpoint doesn't exist, never will succeed" (no HTTP status is
        // exposed), so a small retry budget is the safest general fix: give
        // a real transient failure a couple of chances, then accept polling
        // as the steady state instead of retrying indefinitely.
        streamRetryCountRef.current += 1;
        if (streamRetryCountRef.current <= 2) {
          setTimeout(() => {
            if (enabled) {
              connectToStream();
            }
          }, 5000);
        }
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to connect to log stream:', error);
      setIsConnected(false);
      startPolling();
    }
  }, [hasRealSession, sessionId, enabled, containerPort, startPolling, stopPolling]);

  // Connect/disconnect from stream. The session id is known (`<project>-<service path>`) before the runner has the
  // session: the panel mounts with the render that creates it, so opening the stream at once answered 404 (and the
  // status poll with it) on every new preview, seen live on kind. Ask /status first, a moment after mount, and open
  // the stream only once the runner knows the session; a 404 is retried with a growing pause, a few times, then
  // left alone (the session does not exist: no polling, no stream).
  useEffect(() => {
    // Fresh session (or a genuine re-enable) gets its own retry budget.
    streamRetryCountRef.current = 0;
    // A different session/port means a different container's log buffer —
    // any earlier replay we diffed against belongs to the old one.
    lastReplayedLogsRef.current = [];
    let cancelled = false;
    let probeTimer = null;
    let attempt = 0;
    const probe = async () => {
      probeTimer = null;
      let kind = 'retry';
      try {
        const statusUrl = getContainerApiUrl(containerPort, `/api/preview/${encodeURIComponent(sessionId)}/status`);
        const res = await fetch(statusUrl, { headers: { Accept: 'application/json' }, redirect: 'manual' });
        kind = classifyStatusResponse(res);
      } catch {
        kind = 'retry';
      }
      if (cancelled) return;
      if (kind === 'ok') {
        connectToStream();
        return;
      }
      // Not the runner (a redirect to the sign-in, a page): nothing to poll.
      if (kind === 'signed-out') return;
      attempt += 1;
      if (attempt >= MAX_SESSION_PROBES) {
        devLog(`📡 No preview session "${sessionId}" on the runner, not polling its logs`);
        return;
      }
      probeTimer = setTimeout(probe, backoffDelay(attempt, { baseMs: FIRST_PROBE_DELAY_MS, maxMs: 15000 }));
    };
    if (enabled && hasRealSession) {
      probeTimer = setTimeout(probe, FIRST_PROBE_DELAY_MS);
    }

    return () => {
      cancelled = true;
      if (probeTimer) clearTimeout(probeTimer);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionId, containerPort]);
  // Note: We include sessionId and containerPort directly instead of connectToStream
  // to avoid the function reference changing and causing unnecessary reconnections

  // Clear logs
  const handleClearLogs = () => {
    setLogs([]);
  };

  // Toggle auto-scroll
  const handleToggleAutoScroll = () => {
    setAutoScroll(prev => !prev);
  };

  // Send logs to AI for help. Previously built ONE giant string (a short
  // request glued to the full raw log dump, timestamps and all) and sent
  // the whole thing as the visible chat message — every click filled the
  // transcript with an unreadable wall of text. Now sends a short, normal
  // user-facing message and hands the raw logs to the agent separately, via
  // the chat widget's `pageContext` (sent as `state_delta.page_context`,
  // injected into the agent's own system instructions — never rendered as
  // message text, see ChatWidgetProps' own doc). `onAskAI` now receives
  // `{message, logText}` instead of one merged prompt string.
  const handleAskAI = () => {
    if (!onAskAI) return;
    if (logs.length === 0 && !pendingErrorText) return;

    const logText = logs
      .map(log => `[${formatTime(log.timestamp)}] [${log.type}] ${stripAnsi(log.line)}`)
      .join('\n');

    // Show the actual error in the visible chat message instead of a generic
    // sentence, so the agent (and the user, reading the transcript) jumps
    // straight to the real problem instead of guessing from the full log
    // dump. pendingErrorText (the precise, already-extracted error — e.g. a
    // Vite compile-overlay error or a build-failure response) wins when
    // available; otherwise fall back to the most recent error-type log lines.
    const errorLines = pendingErrorText || logs
      .filter(log => log.type === 'stderr' || log.type === 'client-error')
      .slice(-8)
      .map(log => stripAnsi(log.line))
      .join('\n')
      .trim();

    const message = errorLines
      ? `I'm getting this error in my preview, please fix it:\n\n${errorLines}`
      : "I'm getting errors in my preview — please check what's wrong and fix them.";

    onAskAI({ message, logText });
    onPendingErrorConsumed?.();
  };

  // Strip ANSI color codes from log lines. Declared here (before
  // filteredLogs/groupedLogs below, which both call it) rather than further
  // down where it used to live — using it above its own declaration hit its
  // temporal dead zone and crashed this component's every render
  // ("Cannot access 'stripAnsi' before initialization"), live-confirmed the
  // moment the log-grouping code above started calling it early.
  const stripAnsi = (str) => {
    if (!str) return '';
    // eslint-disable-next-line no-control-regex
    return str.replace(/\x1B\[[0-9;]*[A-Za-z]/g, '');
  };

  // Filter logs based on selected filter
  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    // preview-runner now types its build lines `build`; they were `stdout` before and still belong here.
    if (filter === 'stdout') return log.type === 'stdout' || log.type === 'build';
    if (filter === 'stderr') return log.type === 'stderr';
    if (filter === 'client') return log.type === 'client-error';
    if (filter === 'errors') {
      // Include both server-side and client-side errors
      if (log.type === 'client-error') return true;
      // Simple error detection - contains 'error', 'failed', etc.
      const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
      return errorPatterns.test(log.line);
    }
    return true;
  });

  // A raw dev-server error dump is two things that make it unreadable as a
  // flat list: (1) the exact same line ("[vite] server restarted.") repeated
  // back-to-back many times, and (2) a stack trace exploded into one row
  // per frame. Group both into a single collapsed row instead of dozens of
  // near-identical/individually-meaningless rows.
  const isStackFrameLine = (line) => /^\s*at\s+\S.*\(.+\)\s*$/.test(line || '');

  const groupedLogs = [];
  filteredLogs.forEach((log) => {
    const text = stripAnsi(log.line || '');
    const prev = groupedLogs[groupedLogs.length - 1];

    if (prev && !prev.isStackGroup && prev.type === log.type && stripAnsi(prev.line || '') === text) {
      prev.repeatCount = (prev.repeatCount || 1) + 1;
      prev.timestamp = log.timestamp; // keep the most recent occurrence's time
      return;
    }

    if (isStackFrameLine(text)) {
      if (prev && prev.isStackGroup && prev.type === log.type) {
        prev.stackLines.push(text);
        return;
      }
      groupedLogs.push({ ...log, isStackGroup: true, stackLines: [text] });
      return;
    }

    groupedLogs.push({ ...log, repeatCount: 1 });
  });

  // Detect if a log line is a warning (starts with 'warn' prefix)
  const isWarningLine = (line) => /^\s*warn[\s\-:]/i.test(line || '');

  // Count errors for collapsed bar indicator (warnings excluded)
  const errorCount = logs.filter(log => {
    if (log.type === 'client-error') return true;
    if (isWarningLine(log.line)) return false; // warnings are not errors
    // Detect error patterns in log lines
    const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
    return errorPatterns.test(log.line);
  }).length;

  // Count warnings separately
  const warningCount = logs.filter(log => {
    if (log.type === 'client-error') return false;
    return isWarningLine(log.line);
  }).length;

  // Detect build-success lines (Next.js / other frameworks)
  const isBuildSuccessLine = (line) => {
    if (!line) return false;
    return /✓\s*Ready|compiled (client and server )?successfully|\bReady in\b|ready started server|\bReady\b.*started/i.test(line);
  };

  // Find the index of the last build-success log entry
  const lastSuccessIndex = logs.reduce((acc, log, idx) => {
    if (log.type !== 'client-error' && isBuildSuccessLine(log.line)) return idx;
    return acc;
  }, -1);

  // Active errors = errors still unresolved by a successful build.
  // Whether historical errors count as active depends on what the server reports:
  //   buildStatus 'completed' → last build succeeded, historical errors are resolved
  //   anything else (failed / null / building) → historical errors are still active
  const historicalBuildSucceeded = historicalBuildStatus === 'completed';

  const activeErrorCount = logs.filter((log, idx) => {
    if (log.isHistorical) {
      if (historicalBuildSucceeded) return false; // resolved by a later successful build
      if (log.type === 'client-error') return true;
      if (isWarningLine(log.line)) return false;
      const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
      return errorPatterns.test(log.line);
    }
    if (idx <= lastSuccessIndex) return false;
    if (log.type === 'client-error') return true;
    if (isWarningLine(log.line)) return false;
    const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
    return errorPatterns.test(log.line);
  }).length;

  const staleErrorCount = logs.filter((log, idx) => {
    if (log.isHistorical) {
      if (!historicalBuildSucceeded) return false; // still active, not stale
      if (log.type === 'client-error') return true;
      if (isWarningLine(log.line)) return false;
      const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
      return errorPatterns.test(log.line);
    }
    if (lastSuccessIndex < 0) return false;
    if (idx > lastSuccessIndex) return false;
    if (log.type === 'client-error') return true;
    if (isWarningLine(log.line)) return false;
    const errorPatterns = /error|failed|exception|undefined|cannot|unexpected/i;
    return errorPatterns.test(log.line);
  }).length;

  // Fire onBuildError once per session
  useEffect(() => {
    // Fire onBuildError the first time errors appear in this session.
    // We intentionally read onBuildError and logs through refs so that
    // a Canvas.jsx re-render (e.g. caused by closing the modal) does NOT
    // add onBuildError/logs to this effect's deps and cannot immediately
    // reopen the modal right after the user has dismissed it.
    if (activeErrorCount > 0 && !buildErrorFiredRef.current && onBuildErrorRef.current) {
      buildErrorFiredRef.current = true;

      // Collect error log lines (strip ANSI codes, skip warnings)
      // eslint-disable-next-line no-control-regex
      const ansiRegex = /\x1B\[[0-9;]*[A-Za-z]/g;
      const errorLines = latestLogsRef.current
        .filter(log => {
          if (log.type === 'client-error') return true;
          if (!log.line) return false;
          if (/^\s*warn[\s\-:]/i.test(log.line)) return false;
          return /error|failed|exception|undefined|cannot|unexpected/i.test(log.line);
        })
        .map(log => (log.line || '').replace(ansiRegex, ''))
        .slice(0, 40)
        .join('\n');

      onBuildErrorRef.current(errorLines || 'Errors detected in preview logs.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeErrorCount]); // onBuildError and logs are accessed via refs on purpose

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (!enabled || !hasRealSession || hideUI) {
    return null;
  }

  return (
    <div className={`preview-logs preview-logs-${position} ${isExpanded ? 'expanded' : 'collapsed'}`}>
      {/* Header bar - clickable to toggle expansion */}
      <div 
        className="preview-logs-header"
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <div className="preview-logs-title">
          <span className="preview-logs-icon">{'📋'}</span>
          <span>{'Preview Logs'}</span>
          {(isConnected || isPolling) && <span className="preview-logs-status connected" title={isPolling ? 'Polling for logs' : 'Live log stream'}>{'●'}</span>}
          {!isConnected && !isPolling && <span className="preview-logs-status disconnected">{'●'}</span>}
          <span className="preview-logs-count">{'('}{filteredLogs.length}{')'}</span>
          {activeErrorCount > 0 && (
            <span className="preview-logs-error-badge" title={`${activeErrorCount} active error${activeErrorCount !== 1 ? 's' : ''} detected`}>
              {'⚠️ '}{activeErrorCount}{' error'}{activeErrorCount !== 1 ? 's' : ''}
            </span>
          )}
          {activeErrorCount === 0 && staleErrorCount > 0 && (
            <span className="preview-logs-resolved-badge" title={`${staleErrorCount} error${staleErrorCount !== 1 ? 's' : ''} resolved — build succeeded`}>
              {'✓ '}{staleErrorCount}{' resolved'}
            </span>
          )}
          {warningCount > 0 && (
            <span className="preview-logs-warning-badge" title={`${warningCount} warning${warningCount !== 1 ? 's' : ''}`}>
              {'⚠ '}{warningCount}{' warning'}{warningCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="preview-logs-actions" onClick={(e) => e.stopPropagation()}>
          {/* Rendered outside the isExpanded gate below so a pending error stays
              visible (and highlighted) even while the log panel is collapsed —
              this is the "just highlight the button, no modal" fix. */}
          {onAskAI && hasPendingError && (
            <button
              className="preview-logs-btn preview-logs-ask-ai preview-logs-ask-ai-pending"
              onClick={handleAskAI}
              title="A build error was detected — click to have AI fix it"
              type="button"
            >
              {'🤖 Ask AI'}
            </button>
          )}
          {isExpanded && (
            <>
              {onAskAI && !hasPendingError && logs.length > 0 && (
                <button
                  className="preview-logs-btn preview-logs-ask-ai"
                  onClick={handleAskAI}
                  title="Ask AI to fix errors"
                  type="button"
                >
                  {'🤖 Ask AI'}
                </button>
              )}

              <select
                className="preview-logs-filter"
                onChange={(e) => setFilter(e.target.value)}
                value={filter}
              >
                <option value="all">{'All'}</option>
                <option value="stdout">{'stdout'}</option>
                <option value="stderr">{'stderr'}</option>
                <option value="client">{'Client Errors'}</option>
                <option value="errors">{'All Errors'}</option>
              </select>
              
              <button
                className={`preview-logs-btn preview-logs-icon-btn ${autoScroll ? 'active' : ''}`}
                onClick={handleToggleAutoScroll}
                title={autoScroll ? 'Disable auto-scroll' : 'Enable auto-scroll'}
                type="button"
              >
                {/* Scroll-to-bottom icon */}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 3v14"/>
                  <path d="M5 14l7 7 7-7"/>
                  <path d="M5 21h14"/>
                </svg>
              </button>
              
              <button
                className="preview-logs-btn preview-logs-icon-btn"
                onClick={handleClearLogs}
                title="Clear logs"
                type="button"
              >
                {/* Trash icon */}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </button>
            </>
          )}
          
          <button
            className="preview-logs-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            title={isExpanded ? 'Collapse logs' : 'Expand logs'}
            type="button"
          >
            {isExpanded ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {/* Logs content */}
      {isExpanded && (
        <div className="preview-logs-content" ref={logsContainerRef}>
          {groupedLogs.length === 0 ? (
            <div className="preview-logs-empty">
              {isConnected || isPolling ? 'Waiting for logs...' : 'Connecting to log stream...'}
            </div>
          ) : (
            <div className="preview-logs-lines">
              {groupedLogs.map((log, index) => {
                if (log.isStackGroup) {
                  const isOpen = expandedStackGroups.has(index);
                  const visibleFrames = isOpen ? log.stackLines : log.stackLines.slice(0, 1);
                  const hiddenCount = log.stackLines.length - visibleFrames.length;
                  return (
                    <div
                      className={`preview-log-line preview-log-${log.type}${log.isHistorical ? ' preview-log-historical' : ''}`}
                      key={index}
                    >
                      <span className="preview-log-time">
                        {log.isHistorical ? 'replayed' : formatTime(log.timestamp)}
                      </span>
                      <span className="preview-log-type-indicator">
                        {log.type === 'stderr' ? '⚠' : log.type === 'client-error' ? '🔴' : '•'}
                      </span>
                      <span className="preview-log-text">
                        {visibleFrames.join('\n')}
                        {hiddenCount > 0 && (
                          <button
                            type="button"
                            className="preview-log-stack-toggle"
                            onClick={() => setExpandedStackGroups((prev) => new Set(prev).add(index))}
                          >
                            {`▸ ${hiddenCount} more stack frame${hiddenCount !== 1 ? 's' : ''}`}
                          </button>
                        )}
                        {isOpen && log.stackLines.length > 1 && (
                          <button
                            type="button"
                            className="preview-log-stack-toggle"
                            onClick={() => setExpandedStackGroups((prev) => {
                              const next = new Set(prev);
                              next.delete(index);
                              return next;
                            })}
                          >
                            {'▾ collapse stack'}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                }

                return (
                  <div
                    className={`preview-log-line preview-log-${log.type}${log.isHistorical ? ' preview-log-historical' : ''}`}
                    key={index}
                  >
                    <span className="preview-log-time">
                      {log.isHistorical ? 'replayed' : formatTime(log.timestamp)}
                    </span>
                    <span className="preview-log-type-indicator">
                      {log.type === 'stderr' ? '⚠' : log.type === 'client-error' ? '🔴' : '•'}
                    </span>
                    <span className="preview-log-text">
                      {stripAnsi(log.line)}
                      {log.repeatCount > 1 && (
                        <span className="preview-log-repeat-badge" title={`Repeated ${log.repeatCount} times`}>
                          {` ×${log.repeatCount}`}
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

PreviewLogs.propTypes = {
  containerPort: PropTypes.number,
  enabled: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onAskAI: PropTypes.func,
  onBuildError: PropTypes.func,
  hasPendingError: PropTypes.bool,
  pendingErrorText: PropTypes.string,
  onPendingErrorConsumed: PropTypes.func,
  onToggleExpanded: PropTypes.func,
  position: PropTypes.oneOf(['top', 'bottom', 'inline']),
  sessionId: PropTypes.string,
};

export default PreviewLogs;
